/**
 * What a co-op session looks like to anything that draws it.
 *
 * Two surfaces show a session: the lobby a player sees and the diagnostics panel a developer sees.
 * They show different amounts of the same thing, so the thing lives here and neither one keeps its
 * own idea of who is connected. Pure reducer, so both surfaces can be reasoned about by reading one
 * file and tested without a network.
 *
 * `members` and `roster` are two views of the same room and neither replaces the other. A member is
 * a peer: an opaque id, a channel, a candidate type, and only the ones this machine holds a
 * connection to, which for a guest is exactly one. A roster row is a seat: a player number, a name
 * and whether they are ready, for everybody in the session including the guests a guest cannot see.
 * The player list is the roster, the diagnostics panel is the members, and conflating them would
 * leave a guest's player list showing one other person in a game of four.
 *
 * The log is a ring. A session that flaps for ten minutes must not grow without bound, and the
 * oldest line stops being interesting long before that.
 */

import { IDLE_STATUS } from '@/net/session/connectionLifecycle';
import { DEFAULT_SESSION_RULES } from '@/net/session/sessionRules';
import type { SessionRules } from '@/net/session/sessionRules';
import type { ConnectionStatus, PeerId, SessionRole, TransportPeer } from '@/net/transportContract';
import type { EndingReason, RosterSeat } from '@/net/wire/lobbyFrame';

const LOG_LIMIT = 40;

export interface LobbyMember {
  readonly id: PeerId;
  readonly role: SessionRole;
  readonly snapshotChannelReady: boolean;
  readonly relayed: boolean | null;
}

export interface LobbyState {
  readonly status: ConnectionStatus;
  /** Formatted for reading aloud, empty until a session exists. */
  readonly code: string;
  readonly role: SessionRole | null;
  readonly selfId: PeerId | null;
  /** The peers this machine holds a connection to. The diagnostics panel's view. */
  readonly members: readonly LobbyMember[];
  /** Everybody in the session, host's word. The player list's view. */
  readonly roster: readonly RosterSeat[];
  /** How the room is being played. The host's word, and what the game is launched under. */
  readonly rules: SessionRules;
  /** Which player this machine is, once the host has said. */
  readonly localPlayer: number | null;
  /** Set once, when the session is over for a reason nobody here can undo. */
  readonly ending: EndingReason | null;
  /** Milliseconds left of the reconnect attempt, or null when nothing is being waited for. */
  readonly rejoiningMsLeft: number | null;
  /** Newest last, capped. Shown in full by the diagnostics panel, not by the lobby. */
  readonly log: readonly string[];
}

export const EMPTY_LOBBY: LobbyState = {
  status: IDLE_STATUS,
  code: '',
  role: null,
  selfId: null,
  members: [],
  roster: [],
  rules: DEFAULT_SESSION_RULES,
  localPlayer: null,
  ending: null,
  rejoiningMsLeft: null,
  log: [],
};

export type LobbyEvent =
  | { readonly kind: 'opening'; readonly role: SessionRole }
  | {
      readonly kind: 'opened';
      readonly code: string;
      readonly role: SessionRole;
      readonly selfId: PeerId;
    }
  | { readonly kind: 'status'; readonly status: ConnectionStatus }
  | { readonly kind: 'peer'; readonly peer: TransportPeer }
  | { readonly kind: 'peerLeft'; readonly id: PeerId }
  | { readonly kind: 'roster'; readonly seats: readonly RosterSeat[] }
  | { readonly kind: 'rules'; readonly rules: SessionRules }
  | { readonly kind: 'seated'; readonly playerId: number }
  | { readonly kind: 'ending'; readonly reason: EndingReason }
  | { readonly kind: 'rejoining'; readonly msLeft: number | null }
  | { readonly kind: 'note'; readonly text: string }
  | { readonly kind: 'left' };

export function reduceLobby(state: LobbyState, event: LobbyEvent): LobbyState {
  switch (event.kind) {
    case 'opening':
      return { ...EMPTY_LOBBY, role: event.role, log: note(state.log, 'Opening a session.') };

    case 'opened':
      return {
        ...state,
        code: event.code,
        role: event.role,
        selfId: event.selfId,
        log: note(state.log, `Session ${event.code} as ${event.role}.`),
      };

    case 'status':
      return { ...state, status: event.status, log: note(state.log, event.status.detail) };

    // A peer is announced again for anything the transport learns about it, most of it invisible
    // here: the same row twice is not news, and redrawing the lobby for it is how a host loses the
    // press it just made.
    case 'peer': {
      const members = withMember(state.members, event.peer);
      if (members === state.members) return state;
      return {
        ...state,
        members,
        log: note(state.log, `${event.peer.role} ${short(event.peer.id)} is here.`),
      };
    }

    case 'peerLeft':
      return {
        ...state,
        members: state.members.filter((member) => member.id !== event.id),
        log: note(state.log, `${short(event.id)} left.`),
      };

    // Whole state, never a delta: it is the only thing a guest learns about another guest from, and
    // the host sends it often enough that a merge would only be a way to get it wrong.
    case 'roster':
      return { ...state, roster: event.seats };

    // Full state, like the roster, and for the same reason: one byte that cannot drift is cheaper
    // to send whole than a change nobody can verify.
    case 'rules':
      return { ...state, rules: event.rules };

    case 'seated':
      return {
        ...state,
        localPlayer: event.playerId,
        log: note(state.log, `Seated as player ${String(event.playerId)}.`),
      };

    case 'ending':
      return { ...state, ending: event.reason, rejoiningMsLeft: null };

    case 'rejoining':
      return { ...state, rejoiningMsLeft: event.msLeft };

    case 'note':
      return { ...state, log: note(state.log, event.text) };

    case 'left':
      return { ...EMPTY_LOBBY, log: note(state.log, 'Left the session.') };
  }
}

/**
 * The same peer twice is an update, never a second row, and the same row twice is neither: the
 * array comes back by identity when nothing moved, which is what lets the reducer say so.
 */
function withMember(members: readonly LobbyMember[], peer: TransportPeer): readonly LobbyMember[] {
  const member: LobbyMember = {
    id: peer.id,
    role: peer.role,
    snapshotChannelReady: peer.snapshotChannelReady,
    relayed: peer.relayed,
  };
  const at = members.findIndex((existing) => existing.id === peer.id);
  const seated = members[at];
  if (seated === undefined) return [...members, member];
  if (sameMember(seated, member)) return members;
  return members.map((existing, index) => (index === at ? member : existing));
}

function sameMember(a: LobbyMember, b: LobbyMember): boolean {
  return (
    a.role === b.role &&
    a.snapshotChannelReady === b.snapshotChannelReady &&
    a.relayed === b.relayed
  );
}

function note(log: readonly string[], text: string): readonly string[] {
  const next = [...log, text];
  return next.length <= LOG_LIMIT ? next : next.slice(next.length - LOG_LIMIT);
}

/** Peer ids are long random strings. Enough of one to tell two players apart. */
export function short(id: PeerId): string {
  return id.slice(0, 6);
}
